/**
 * Sync Status Indicator
 *
 * Shows current sync status in the UI:
 * - Online/Offline
 * - Auth health status
 * - Syncing indicator
 * - Pending changes count
 * - Failed changes count
 * - Last sync time
 * - Manual sync button
 * - Retry/Clear failed actions
 */

import { useState } from 'react';
import { useSync } from '../hooks/useSync';
import { resetAuthState } from '../lib/supabase';
import { fixNullPropertyValues, clearFailedPropertySync } from '../utils/fixNullPropertyValues';
import { normalizePriorityValues } from '../utils/normalizePriorityValues';
import {
  Wifi,
  WifiOff,
  Key,
  RefreshCw,
  XCircle,
  Clock,
  CheckCircle2,
  Settings,
  Wrench,
  Trash2,
  AlertTriangle,
  Flag
} from 'lucide-react';
import './SyncStatus.css';

export default function SyncStatus() {
  const {
    isOnline,
    isSyncing,
    pendingChanges,
    failedChanges,
    lastSyncAt,
    authHealth,
    syncNow,
    clearAllPending,
    clearFailedEntries,
    retryFailedEntries,
    checkAuthHealth
  } = useSync();

  const [showMenu, setShowMenu] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);

  const getStatusIcon = () => {
    const size = 14;
    if (!isOnline) return <WifiOff size={size} />;
    if (authHealth === 'needs_login') return <Key size={size} />;
    if (authHealth === 'checking' || isRecovering) return <RefreshCw size={size} className="spinning" />;
    if (isSyncing) return <RefreshCw size={size} className="spinning" />;
    if (failedChanges > 0) return <XCircle size={size} />;
    if (pendingChanges > 0) return <Clock size={size} />;
    return <CheckCircle2 size={size} />;
  };

  const getStatusText = () => {
    if (!isOnline) return 'Offline';
    if (authHealth === 'needs_login') return 'Login required';
    if (authHealth === 'checking') return 'Checking...';
    if (isRecovering) return 'Recovering...';
    if (isSyncing) return 'Syncing...';
    if (failedChanges > 0) return `${failedChanges} failed`;
    if (pendingChanges > 0) return `${pendingChanges} pending`;
    if (authHealth === 'recovered') return 'Recovered';
    return 'Synced';
  };

  const formatLastSync = () => {
    if (!lastSyncAt) return 'Never';

    const now = new Date();
    const diff = now.getTime() - lastSyncAt.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (seconds < 60) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return lastSyncAt.toLocaleDateString();
  };

  const handleRetryFailed = async () => {
    setShowMenu(false);
    const count = await retryFailedEntries();
    if (count > 0) {
      console.log(`Retrying ${count} failed entries`);
    }
  };

  const handleClearFailed = async () => {
    if (confirm(`Clear ${failedChanges} permanently failed sync items? This cannot be undone.`)) {
      setShowMenu(false);
      await clearFailedEntries();
    }
  };

  const handleClearAll = async () => {
    const total = pendingChanges + failedChanges;
    if (confirm(`Clear ALL ${total} pending sync items? This will lose any unsynced changes and cannot be undone.`)) {
      setShowMenu(false);
      await clearAllPending();
    }
  };

  const handleFixAuth = async () => {
    setShowMenu(false);
    setIsRecovering(true);
    try {
      const result = await checkAuthHealth();
      if (result === 'healthy' || result === 'recovered') {
        console.log('Auth recovered successfully, syncing...');
        await syncNow();
      } else {
        console.warn('Auth recovery failed, may need to re-login');
      }
    } finally {
      setIsRecovering(false);
    }
  };

  const handleResetAuth = () => {
    if (confirm('This will log you out. You will need to sign in again. Continue?')) {
      setShowMenu(false);
      resetAuthState();
      window.location.reload();
    }
  };

  const handleFixData = async () => {
    setShowMenu(false);
    console.log('[SyncStatus] Running comprehensive data fix...');
    try {
      // Step 1: Fix null values in IndexedDB
      console.log('[SyncStatus] Step 1: Fixing null property values...');
      const fixResult = await fixNullPropertyValues();

      // Step 2: Clear failed sync queue entries (both local and remote)
      console.log('[SyncStatus] Step 2: Clearing failed sync queue entries...');
      const clearResult = await clearFailedPropertySync();

      // Show comprehensive results
      const messages = [];

      if (fixResult.fixed > 0) {
        messages.push(`✅ Fixed ${fixResult.fixed} properties with null values`);
      } else {
        messages.push(`✅ All properties already have valid values`);
      }

      if (clearResult.local > 0 || clearResult.remote > 0) {
        messages.push(`🗑️ Cleared ${clearResult.local} local + ${clearResult.remote} remote sync queue entries`);
      } else {
        messages.push(`✅ No failed sync entries to clear`);
      }

      if (fixResult.errors.length > 0) {
        messages.push(`⚠️ ${fixResult.errors.length} errors occurred (check console)`);
        console.error('[SyncStatus] Fix errors:', fixResult.errors);
      }

      messages.push('\n💡 Tip: Click the sync button to retry syncing with fixed data');

      alert(messages.join('\n'));
    } catch (error) {
      console.error('[SyncStatus] Data fix failed:', error);
      alert('❌ Failed to fix data. Check console for details.');
    }
  };

  const handleNormalizePriorities = async () => {
    setShowMenu(false);
    try {
      const result = await normalizePriorityValues();
      const messages = [
        result.fixed > 0
          ? `✅ Rewrote ${result.fixed} of ${result.scanned} priority values from 'urgent' to 'critical'`
          : `✅ All ${result.scanned} priority values already valid`,
      ];
      if (result.errors.length > 0) {
        messages.push(`⚠️ ${result.errors.length} errors occurred (check console)`);
        console.error('[SyncStatus] Normalize errors:', result.errors);
      }
      if (result.fixed > 0) {
        messages.push('\n💡 Syncing now so other devices pick up the fix...');
        alert(messages.join('\n'));
        await syncNow();
      } else {
        alert(messages.join('\n'));
      }
    } catch (error) {
      console.error('[SyncStatus] Priority normalization failed:', error);
      alert('❌ Failed to normalize priorities. Check console for details.');
    }
  };

  const authNeedsAttention = authHealth === 'needs_login' || authHealth === 'checking';

  return (
    <div className="sync-status">
      <button
        className={`sync-status-btn ${!isOnline ? 'offline' : ''} ${isSyncing ? 'syncing' : ''} ${failedChanges > 0 ? 'failed' : ''} ${authNeedsAttention ? 'auth-issue' : ''}`}
        onClick={authHealth === 'needs_login' ? handleFixAuth : syncNow}
        disabled={!isOnline || isSyncing || isRecovering}
        title={`Last synced: ${formatLastSync()}${authHealth === 'needs_login' ? '\nAuth issue - click to fix' : ''}${pendingChanges > 0 ? `\n${pendingChanges} pending` : ''}${failedChanges > 0 ? `\n${failedChanges} failed` : ''}`}
      >
        <span className="sync-icon">{getStatusIcon()}</span>
        <span className="sync-text">{getStatusText()}</span>
      </button>

      {/* Always rendered: repair actions like priority normalization must be
          reachable when the queue is healthy — gating on hasIssues hid them
          exactly when legacy data (not sync failures) was the problem. */}
      <div className="sync-actions">
          <button
            className="sync-menu-btn"
            onClick={() => setShowMenu(!showMenu)}
            title="Sync options"
          >
            <Settings size={14} />
          </button>

          {showMenu && (
            <div className="sync-menu">
              {authHealth === 'needs_login' && (
                <>
                  <button className="sync-menu-item" onClick={handleFixAuth}>
                    <Wrench size={14} /> Try to fix auth
                  </button>
                  <button className="sync-menu-item danger" onClick={handleResetAuth}>
                    <Key size={14} /> Re-login
                  </button>
                </>
              )}
              {failedChanges > 0 && (
                <>
                  <button className="sync-menu-item" onClick={handleRetryFailed}>
                    <RefreshCw size={14} /> Retry {failedChanges} failed
                  </button>
                  <button className="sync-menu-item" onClick={handleFixData} title="Fix properties with null values (common cause of sync failures)">
                    <Wrench size={14} /> Fix null data
                  </button>
                  <button className="sync-menu-item danger" onClick={handleClearFailed}>
                    <Trash2 size={14} /> Clear failed only
                  </button>
                </>
              )}
              {(pendingChanges > 0 || failedChanges > 0) && (
                <button className="sync-menu-item danger" onClick={handleClearAll}>
                  <AlertTriangle size={14} /> Clear all pending
                </button>
              )}
              <button
                className="sync-menu-item"
                onClick={handleNormalizePriorities}
                title="Rewrite stored priority 'urgent' → 'critical' (legacy value the buttons used to write; nothing matches on it)"
              >
                <Flag size={14} /> Fix legacy priorities
              </button>
            </div>
          )}
        </div>
    </div>
  );
}
